import React, { createContext, useState, useContext, useEffect } from 'react';
import { IUser } from '../interfaces/User.interface';

const dummyUser: IUser = {
    firstName: "",
    lastName: "",
    email: "",
    userId: "",
    username: "",
    role: undefined,
}

const AuthContext = createContext({
  loggedIn: false,
  login: (user: IUser) => {},
  logout: () => {},
  source: "",
  setSource: (source: string) => {},
  user: dummyUser,
  isInitialized: false,
});


interface LoadingProviderInterface{
    children: React.ReactNode
}

export const AuthProvider: React.FC<LoadingProviderInterface> = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState<IUser>(dummyUser);
  const [isInitialized, setIsInitialized] = useState(false);

  const [source, setSource] = useState<string>(() => {
    const storedSource = localStorage.getItem('source');
    return storedSource ? storedSource : "";
  });

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedLoggedIn = localStorage.getItem('loggedIn');
        
        // Only attempt to restore session if localStorage indicates logged in
        if (storedLoggedIn && JSON.parse(storedLoggedIn)) {
          try {
            // Validate session with backend
            const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/auth/checkAuth', {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include',
            });

            if (response.ok) {
              // Session is valid, restore user data
              const userData = await response.json();
              setUser(userData);
              setLoggedIn(true);
            } else {
              // Session is invalid, clear localStorage
              localStorage.removeItem('loggedIn');
              localStorage.removeItem('user');
              setLoggedIn(false);
              setUser(dummyUser);
            }
          } catch (error) {
            // Network error, clear localStorage to be safe
            console.warn('Failed to validate session:', error);
            localStorage.removeItem('loggedIn');
            localStorage.removeItem('user');
            setLoggedIn(false);
            setUser(dummyUser);
          }
        } else {
          // Not logged in according to localStorage
          setLoggedIn(false);
          setUser(dummyUser);
        }
      } finally {
        // Mark initialization as complete
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  const login = (user: IUser) => {
    setUser(user)
    setLoggedIn(true);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('loggedIn', JSON.stringify(true));
  };

  const logout = () => {
    setLoggedIn(false);
    setUser(dummyUser);
    // Clear ALL auth-related localStorage items, not just loggedIn
    localStorage.removeItem('loggedIn');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ loggedIn, login, logout, user, source, setSource, isInitialized }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);